import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, Wallet } from 'lucide-react';
import Navbar from '@/component/navbar';

type ServiceType = 'incorporate' | 'operate';

const CheckItem = ({ text, color }: { text: string; color: string }) => (
  <div className="flex items-start gap-2.5">
    <svg className="w-4 h-4 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="9" stroke={color} strokeWidth="1.5" fill={`${color}22`} />
      <path d="M6.5 10.5L9 13L13.5 7.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    <span className="text-sm text-gray-600 leading-snug">{text}</span>
  </div>
);

const IncorporateIllustration = () => (
  <div className="relative w-full h-full flex items-center justify-center">
    <div className="absolute rounded-2xl" style={{ width: '83%', height: '78%', border: '2.5px solid #F59E0B' }} />
    <div className="absolute rounded-xl flex items-center justify-center" style={{ width: '54%', height: '60%', border: '2px solid #F59E0B', backgroundColor: '#FDE68A' }}>
      <Rocket className="w-10 h-10 text-amber-600" strokeWidth={1.5} />
    </div>
  </div>
);

const OperateIllustration = () => (
  <div className="relative w-full h-full flex items-center justify-center">
    <div className="absolute" style={{ width: '90%', height: '85%', borderRadius: '50%', border: '2px solid #FCA5A5' }} />
    <div className="absolute" style={{ width: '62%', height: '60%', borderRadius: '50%', border: '2px solid #F87171' }} />
    <Wallet className="w-14 h-14 text-red-400" strokeWidth={1.5} />
  </div>
);

export default function SetupService() {
  const [selected, setSelected] = useState<ServiceType | null>(null);
  const [showOperateModal, setShowOperateModal] = useState(false);
  const navigate = useNavigate();

  const handleCardClick = (id: ServiceType) => {
    if (id === 'operate') {
      setShowOperateModal(true);
    } else {
      setSelected(id);
    }
  };

  const handleOperateConfirm = () => {
    localStorage.setItem('setupService', 'operate');
    window.location.href = '/dashboard';
  };

  const handleContinue = () => {
    if (!selected) return;
    localStorage.setItem('setupService', selected);
    navigate('/setup-business-type');
  };

  const cards: {
    id: ServiceType;
    title: string;
    subtitle: string;
    features: string[];
    cta: string;
    accentColor: string;
    accentAlpha: string;
    bgColor: string;
    illustration: ReactNode;
  }[] = [
    {
      id: 'incorporate',
      title: 'Incorporate',
      subtitle: "You're launching. We set you up for scale.",
      features: [
        'Company formation — UAE, USA & global hubs',
        'Licensing, tax ID & corporate bank account',
        'Structured for future fundraising from day one',
      ],
      cta: 'Incorporate My Company',
      accentColor: '#F59E0B',
      accentAlpha: '#F59E0B33',
      bgColor: '#FFFBEB',
      illustration: <IncorporateIllustration />,
    },
    {
      id: 'operate',
      title: 'Operate',
      subtitle: "You're running. We keep the back office institutional.",
      features: [
        'Corporate Tax, VAT & regulatory filings',
        'Bookkeeping, payroll & investor-grade reporting',
        'Books always ready for diligence',
      ],
      cta: 'Operate My Company',
      accentColor: '#EF4444',
      accentAlpha: '#EF444433',
      bgColor: '#FEF2F2',
      illustration: <OperateIllustration />,
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 font-sans">
      <Navbar hideSignBtn={false} />

      <div className="flex-1 flex flex-col items-center px-4 py-12">
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            How can we help you today?
          </h1>
          <p className="text-gray-500 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            Select the service that best fits your current business stage. We'll guide you through the rest.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-3xl mb-10">
          {cards.map((card) => {
            const isSelected = selected === card.id;
            return (
              <div
                key={card.id}
                onClick={() => handleCardClick(card.id)}
                className="bg-white rounded-2xl overflow-hidden cursor-pointer flex flex-col"
                style={{
                  border: isSelected ? `2px solid ${card.accentColor}` : '1px solid #E5E7EB',
                  boxShadow: isSelected
                    ? `0 20px 40px -8px ${card.accentColor}30, 0 8px 16px -4px rgba(0,0,0,0.08)`
                    : '0 1px 4px rgba(0,0,0,0.05)',
                  transform: isSelected ? 'translateY(-6px)' : 'translateY(0)',
                  transition: 'border 250ms ease, box-shadow 250ms ease, transform 250ms ease',
                }}
              >
                <div className="h-48 flex items-center justify-center overflow-hidden" style={{ backgroundColor: card.bgColor }}>
                  {card.illustration}
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-1.5">{card.title}</h3>
                  <p className="text-gray-500 text-sm mb-5 leading-relaxed">{card.subtitle}</p>
                  <div className="space-y-3 flex-1 mb-6">
                    {card.features.map((f, i) => (
                      <CheckItem key={i} text={f} color={card.accentColor} />
                    ))}
                  </div>
                  <button
                    type="button"
                    className="w-full py-3 rounded-xl font-semibold text-sm"
                    style={{
                      border: `1.5px solid ${isSelected ? card.accentColor + '66' : '#D1D5DB'}`,
                      color: isSelected ? card.accentColor : '#374151',
                      backgroundColor: isSelected ? `${card.accentColor}0D` : 'white',
                      transition: 'border 250ms ease, color 250ms ease, background-color 250ms ease',
                    }}
                    onClick={(e) => { e.stopPropagation(); handleCardClick(card.id); }}
                  >
                    {card.cta}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between w-full max-w-3xl">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 font-medium text-sm"
            style={{ transition: 'color 200ms ease' }}
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={!selected}
            className="flex items-center gap-2 bg-[#1976D3] text-white font-semibold py-3.5 px-10 rounded-2xl hover:bg-blue-700 active:scale-95 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ transition: 'background-color 200ms ease, box-shadow 200ms ease, transform 100ms ease' }}
          >
            Continue →
          </button>
        </div>
      </div>

      {showOperateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.15)' }}>
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full mx-4">
            <h2 className="text-lg font-bold text-gray-900 mb-3">Run & grow my existing UAE business</h2>
            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              Please confirm that you already have a business with Tax number before proceeding. If you need to form a company and get an Tax number, please go back and select the "Form & Start My New Business" option.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowOperateModal(false)}
                className="px-7 py-2.5 border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleOperateConfirm}
                className="px-7 py-2.5 bg-amber-400 text-gray-900 font-semibold rounded-lg hover:bg-amber-500 transition-colors"
              >
                I have a Company
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
